import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { Modal, Button, Empty, Spin, Typography, Tooltip } from 'antd';
import { SourceEpisode, SourcePanel, SourcePanelAudio } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';
import { sourceStoriesApi } from '../../services/api/sourceStories';
import { useApi } from '../../hooks';
import { SoundOutlined, LoadingOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

interface EpisodePreviewModalProps {
  visible: boolean;
  episode: SourceEpisode & { storyId: string };
  onClose: () => void;
}

const EpisodePreviewModal: React.FC<EpisodePreviewModalProps> = ({
  visible,
  episode,
  onClose,
}) => {
  // State for audio playback
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [playingPanelId, setPlayingPanelId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPanelIndex, setCurrentPanelIndex] = useState(0);
  const [hasPlayedAllAudios, setHasPlayedAllAudios] = useState(false);
  const panelRefs = useRef<Record<string, HTMLDivElement>>({});

  const {
    data: panelsData,
    loading: panelsLoading,
  } = useApi(
    useCallback(() => sourceEpisodesApi.getEpisodePanels(episode.id), [episode.id]),
    { immediate: visible }
  );

  // Fetch story details including audios
  const {
    data: storyData,
    loading: storyLoading,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceStoryDetail(episode.storyId), [episode.storyId]),
    { immediate: visible }
  );

  const panels = panelsData?.data || [];
  const allAudios = storyData?.data?.audios || [];
  
  // Map of panel ID to active audio
  const panelAudios = useMemo(() => {
    const audioMap: Record<string, SourcePanelAudio> = {};
    
    allAudios.forEach(audio => {
      if (audio.status === 'active') {
        audioMap[audio.sourcePanelId] = audio;
      }
    });
    
    return audioMap;
  }, [allAudios]);

  // Function to find the next panel with audio
  const findNextPanelWithAudio = useCallback((startIndex: number) => {
    for (let i = startIndex; i < panels.length; i++) {
      if (panelAudios[panels[i].id]) {
        return { panel: panels[i], index: i };
      }
    }
    return null; // No more panels with audio
  }, [panels, panelAudios]);

  // Function to scroll to a specific panel
  const scrollToPanel = useCallback((panelId: string, index: number) => {
    setCurrentPanelIndex(index);
    if (panelRefs.current[panelId]) {
      panelRefs.current[panelId].scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Function to play audio for a specific panel
  const playPanelAudio = useCallback((panelId: string, index: number) => {
    // Debug log to track when this function is called
    console.log(`playPanelAudio called for panel ${panelId} at index ${index}`);
    
    // Prevent playing the same panel audio if it's already playing
    if (playingPanelId === panelId && isPlaying) {
      console.log('Panel already playing, skipping');
      return;
    }
    
    // Stop current audio if playing
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
      setPlayingPanelId(null);
      setIsPlaying(false);
    }

    // Scroll to the panel
    scrollToPanel(panelId, index);

    const audio = panelAudios[panelId];
    if (!audio) {
      // If this panel has no audio, we'll just scroll to it without trying to play audio
      // This prevents infinite loops when multiple panels in a row have no audio
      return;
    }

    // Create and play the audio
    const newAudio = new Audio(audio.audioUrl);
    setCurrentAudio(newAudio);
    setPlayingPanelId(panelId);
    setIsPlaying(true);

    // Play the audio with error handling
    const playPromise = newAudio.play();
    
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.error('Audio playback failed:', error);
        // If audio fails to play, reset state
        setIsPlaying(false);
        setPlayingPanelId(null);
        setCurrentAudio(null);
        
        // We'll just stay on the current panel instead of trying to auto-advance
        // This prevents potential infinite loops
      });
    }

    // When audio ends
    newAudio.onended = () => {
      setIsPlaying(false);
      setPlayingPanelId(null);
      setCurrentAudio(null);
      
      // Find the next panel with audio
      const nextWithAudio = findNextPanelWithAudio(index + 1);
      
      if (nextWithAudio && nextWithAudio.index > index) {
        // Small delay before playing next audio
        setTimeout(() => {
          playPanelAudio(nextWithAudio.panel.id, nextWithAudio.index);
        }, 500);
      } else {
        // If no more panels with audio, mark that we've played all audios
        setHasPlayedAllAudios(true);
        
        // We'll scroll to the next panel if available
        const nextIndex = index + 1;
        if (nextIndex < panels.length) {
          scrollToPanel(panels[nextIndex].id, nextIndex);
        }
        console.log('Reached the end of all audio panels');
      }
    };
  }, [currentAudio, panelAudios, panels, findNextPanelWithAudio, scrollToPanel, hasPlayedAllAudios, playingPanelId, isPlaying]);

  // Auto-play first panel audio when modal opens
  useEffect(() => {
    // We'll use a separate effect just for handling the initial audio playback
    // This effect runs only when the modal becomes visible and panels are loaded
    if (!visible || panels.length === 0) {
      return; // Don't proceed if modal is not visible or no panels
    }

    // Always scroll to the first panel immediately
    if (panels.length > 0) {
      scrollToPanel(panels[0].id, 0);
    }
    
    // Delay the audio playback slightly to ensure UI rendering is complete
    const timer = setTimeout(() => {
      // Only play if we haven't played all audios yet
      if (!hasPlayedAllAudios) {
        console.log('Starting initial audio playback');
        
        // Find the first panel with audio
        const firstWithAudio = findNextPanelWithAudio(0);
        
        if (firstWithAudio) {
          console.log(`Found first panel with audio: ${firstWithAudio.panel.id}`);
          // Play the first audio
          playPanelAudio(firstWithAudio.panel.id, firstWithAudio.index);
        }
      }
    }, 500);
    
    // Clean up the timer when component unmounts or dependencies change
    return () => clearTimeout(timer);
  // Only run this effect once when the modal becomes visible
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, panels.length]);

  // Stop audio when modal closes
  useEffect(() => {
    if (!visible) {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
        setPlayingPanelId(null);
        setIsPlaying(false);
      }
      // Reset the flag when modal closes so it will play again when reopened
      setHasPlayedAllAudios(false);
    }
  }, [visible, currentAudio]);

  return (
    <Modal
      title={`Preview: ${episode.name}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <div key="audio-status" style={{ float: 'left', display: 'flex', alignItems: 'center' }}>
          {storyLoading ? (
            <Text type="secondary"><LoadingOutlined /> Loading audios...</Text>
          ) : Object.keys(panelAudios).length > 0 ? (
            <Text type="secondary">
              <SoundOutlined /> {Object.keys(panelAudios).length} panel{Object.keys(panelAudios).length !== 1 ? 's' : ''} with audio
            </Text>
          ) : (
            <Text type="secondary">No panel audios available</Text>
          )}
        </div>,
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={400}
      styles={{
        body: {
          padding: 0,
          background: '#f0f2f5',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }
      }}
      centered
      destroyOnClose
    >
      <div style={{ 
        height: '680px', 
        overflowY: 'auto', 
        padding: '20px 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#f0f2f5'
      }}>
        {/* Mobile Phone Simulation Frame - Force 9:16 ratio */}
        <div style={{
          width: '360px',
          height: '640px',
          background: '#000',
          borderRadius: '40px',
          border: '12px solid #333',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          flexShrink: 0
        }}>
          {/* Notch area */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '120px',
            height: '24px',
            background: '#333',
            borderBottomLeftRadius: '15px',
            borderBottomRightRadius: '15px',
            zIndex: 10
          }}></div>

          {/* Screen Content */}
          <div style={{
            width: '100%',
            height: '100%',
            background: '#000',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            scrollbarWidth: 'none', // Hide scrollbar for cleaner look
            msOverflowStyle: 'none'
          }}>
            <style>{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {panelsLoading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                <Spin tip="Loading panels..." />
              </div>
            ) : panels.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                <Empty description="No panels in this episode" />
              </div>
            ) : (
              panels.map((panel, index) => (
                <div 
                  key={panel.id} 
                  ref={(el) => { if (el) panelRefs.current[panel.id] = el }}
                  style={{
                    width: '100%',
                    height: '100%', // Each panel takes full screen height for 9:16 scroll effect
                    position: 'relative',
                    background: '#000',
                    flexShrink: 0,
                    borderBottom: '1px solid #111',
                    border: playingPanelId === panel.id ? '2px solid #1890ff' : 'none'
                  }}>
                  {panel.imagePath ? (
                    <img 
                      src={panel.imagePath} 
                      alt={`Panel ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: '#666'
                    }}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
                      <Text type="secondary">No Image</Text>
                    </div>
                  )}

                  {/* Caption Overlay */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '20px 15px 15px',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                    color: '#fff',
                    textAlign: 'center'
                  }}>
                    {/* Audio indicator */}
                    {panelAudios[panel.id] && (
                      <div 
                        onClick={() => playPanelAudio(panel.id, index)}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          background: playingPanelId === panel.id ? 'rgba(24, 144, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          zIndex: 10
                        }}
                      >
                        {playingPanelId === panel.id && isPlaying ? (
                          <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} spin />
                        ) : (
                          <SoundOutlined style={{ color: '#fff', fontSize: '16px' }} />
                        )}
                      </div>
                    )}
                    <Text style={{ 
                      color: '#fff', 
                      fontSize: '13px', 
                      lineHeight: '1.4',
                      textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                      display: 'block'
                    }}>
                      {panel.localizedCaption || panel.caption}
                    </Text>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EpisodePreviewModal;
